import React, { useState } from "react";
import { toast } from "react-toastify";

const brandBlue = "#3498db";
const tabBaseStyle = {
  padding: "12px 16px",
  fontWeight: 600,
  fontSize: "15px",
  color: "#718096",
  border: "none",
  borderBottom: "2px solid transparent",
  background: "transparent",
  cursor: "pointer",
  transition: "all 0.2s ease",
};
const tabActiveStyle = {
  color: brandBlue,
  borderBottom: `2px solid ${brandBlue}`,
};

// Summary tab: existing scorecard entry UI.
// Receives parent-controlled match list and update callbacks.
const CricketSummaryTab = ({
  cricketMatches,
  availableUsers,
  onAddMatch,
  onUpdateMatch,
  onRemoveMatch,
  onSaveAll,
}) => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <button
          onClick={onAddMatch}
          style={{
            backgroundColor: brandBlue,
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: 600,
            boxShadow: "0 4px 6px rgba(52, 152, 219, 0.3)",
            transition: "all 0.2s ease",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = "#2980b9";
            e.currentTarget.style.transform = "translateY(-2px)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = brandBlue;
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          Add New Cricket Match
        </button>
      </div>

      {cricketMatches.length === 0 && (
        <div className="text-center p-10 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 text-lg">
          No matches added yet. Click the button above to add your first cricket
          match.
        </div>
      )}

      {cricketMatches.map((match, index) => (
        <CricketMatchForm
          key={index}
          match={match}
          matchNumber={index + 1}
          availableUsers={availableUsers}
          onUpdate={(updatedMatch) => onUpdateMatch(index, updatedMatch)}
          onRemove={() => onRemoveMatch(index)}
        />
      ))}

      {cricketMatches.length > 0 && (
        <button
          onClick={onSaveAll}
          className="w-full max-w-[260px] mx-auto block bg-emerald-500 text-white border-2 border-emerald-500 font-semibold py-3 px-4 rounded-lg shadow-md transition-all duration-200 mt-4 hover:bg-white hover:text-emerald-600 hover:-translate-y-0.5"
        >
          Save All Matches
        </button>
      )}
    </div>
  );
};

const CricketMatchForm = ({
  match,
  matchNumber,
  availableUsers,
  onUpdate,
  onRemove,
}) => {
  const [localMatch, setLocalMatch] = useState(match);
  const [activeTab, setActiveTab] = useState("basic"); // basic, teams, scorecard

  const updateParent = (updated) => {
    setLocalMatch(updated);
    onUpdate(updated);
  };

  const addPlayer = (team, userInfo, customName = "") => {
    // If it's a system user (not custom), check if already added to any team
    if (userInfo && userInfo.id) {
      const isInTeam1 = localMatch.team1Players.some(
        (player) => player.userId === userInfo.id
      );
      const isInTeam2 = localMatch.team2Players.some(
        (player) => player.userId === userInfo.id
      );

      if (isInTeam1 || isInTeam2) {
        toast.error(
          `${userInfo.full_name || userInfo.username} is already added to ${
            isInTeam1 ? "Team 1" : "Team 2"
          }`
        );
        return;
      }
    }

    const updated = { ...localMatch };
    const playerId =
      userInfo?.id ||
      `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const player = {
      id: playerId,
      userId: playerId, // Use the same ID for both regular and custom players
      name: userInfo?.full_name || customName || "Guest Player",
      isCustom: !userInfo,
    };

    updated[`${team}Players`].push(player);
    updateParent(updated);
  };

  const removePlayer = (team, playerIndex) => {
    const updated = { ...localMatch };
    updated[`${team}Players`].splice(playerIndex, 1);
    updateParent(updated);
  };

  const movePlayerUp = (team, playerIndex) => {
    if (playerIndex === 0) return; // Can't move first player up
    const updated = { ...localMatch };
    const players = updated[`${team}Players`];
    [players[playerIndex - 1], players[playerIndex]] = [
      players[playerIndex],
      players[playerIndex - 1],
    ];
    updateParent(updated);
  };

  const movePlayerDown = (team, playerIndex) => {
    const updated = { ...localMatch };
    const players = updated[`${team}Players`];
    if (playerIndex === players.length - 1) return; // Can't move last player down
    [players[playerIndex], players[playerIndex + 1]] = [
      players[playerIndex + 1],
      players[playerIndex],
    ];
    updateParent(updated);
  };

  const updateBattingStats = (team, playerId, field, value) => {
    const updated = { ...localMatch };
    const innings = updated.innings[team];

    let playerStat = innings.battingStats.find((p) => p.playerId === playerId);
    if (!playerStat) {
      const player = updated[`${team}Players`].find((p) => p.userId === playerId);
      playerStat = {
        playerId,
        playerName: player?.name || "Unknown",
        runs: 0,
        balls: 0,
        fours: 0,
        sixes: 0,
        isOut: false,
        howOut: "",
        bowlerOut: "",
      };
      innings.battingStats.push(playerStat);
    }

    playerStat[field] = value;
    recalculateInningsTotal(updated, team);
  };

  const updateBowlingStats = (team, playerId, field, value) => {
    const updated = { ...localMatch };
    const innings = updated.innings[team];

    let playerStat = innings.bowlingStats.find((p) => p.playerId === playerId);
    if (!playerStat) {
      const player = updated[`${team}Players`].find((p) => p.userId === playerId);
      playerStat = {
        playerId,
        playerName: player?.name || "Unknown",
        overs: 0,
        balls: 0,
        maidens: 0,
        runs: 0,
        wickets: 0,
        economy: 0,
      };
      innings.bowlingStats.push(playerStat);
    }

    playerStat[field] = value;

    // Calculate economy rate
    if (playerStat.overs > 0) {
      playerStat.economy = (playerStat.runs / playerStat.overs).toFixed(2);
    }

    recalculateInningsTotal(updated, team);
  };

  const recalculateInningsTotal = (updated, team) => {
    const innings = updated.innings[team];

    // Calculate total runs from batting stats
    const battingRuns = innings.battingStats.reduce(
      (total, player) => total + (player.runs || 0),
      0
    );
    const extrasTotal = Object.values(innings.extras).reduce(
      (total, extra) => total + (extra || 0),
      0
    );

    innings.totalRuns = battingRuns + extrasTotal;
    innings.wickets = innings.battingStats.filter((player) => player.isOut).length;

    // Calculate total balls faced and overs from batting stats
    const totalBallsFaced = innings.battingStats.reduce(
      (total, player) => total + (player.balls || 0),
      0
    );
    innings.overs = Math.floor(totalBallsFaced / 6);
    innings.balls = totalBallsFaced % 6;

    // Also calculate from bowling if available
    const totalBallsBowled = innings.bowlingStats.reduce((total, bowler) => {
      return total + (bowler.overs || 0) * 6 + (bowler.balls || 0);
    }, 0);

    // Use the maximum of batting or bowling balls to determine overs
    const maxBalls = Math.max(totalBallsFaced, totalBallsBowled);
    innings.overs = Math.floor(maxBalls / 6);
    innings.balls = maxBalls % 6;

    updateParent(updated);
  };

  return (
    <div className="border border-gray-200 rounded-xl p-6 mb-6 bg-gray-50 shadow-sm">
      <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-6 flex-wrap gap-3">
        <h3 className="text-xl font-semibold text-gray-800">
          Cricket Match #{matchNumber}
        </h3>
        <button
          onClick={onRemove}
          className="bg-red-400 hover:bg-red-500 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
        >
          Remove Match
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {[
          { key: "basic", label: "Basic Info" },
          { key: "teams", label: "Teams" },
          { key: "scorecard", label: "Scorecard" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              ...tabBaseStyle,
              ...(activeTab === tab.key ? tabActiveStyle : {}),
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "basic" && (
        <BasicInfoSection localMatch={localMatch} updateParent={updateParent} />
      )}
      {activeTab === "teams" && (
        <TeamManagementSection
          localMatch={localMatch}
          availableUsers={availableUsers}
          addPlayer={addPlayer}
          removePlayer={removePlayer}
          movePlayerUp={movePlayerUp}
          movePlayerDown={movePlayerDown}
        />
      )}
      {activeTab === "scorecard" && (
        <ScorecardSection
          localMatch={localMatch}
          updateBattingStats={updateBattingStats}
          updateBowlingStats={updateBowlingStats}
          updateParent={updateParent}
          movePlayerUp={movePlayerUp}
          movePlayerDown={movePlayerDown}
        />
      )}

      <MatchSummary localMatch={localMatch} />
    </div>
  );
};

const BasicInfoSection = ({ localMatch, updateParent }) => (
  <div className="space-y-6">
    {/* Cricket Guidance */}
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
      <h4 className="text-lg font-semibold text-blue-800 mb-3">
        🏏 Cricket Match Guide
      </h4>
      <div className="text-blue-700 space-y-2">
        <p>
          <strong>Match Types:</strong> T20 (20 overs), ODI (50 overs), Limited
          Overs (custom)
        </p>
        <p>
          <strong>Team Size:</strong> Standard 11 players, but you can customize
          (2-11 players per team)
        </p>
        <p>
          <strong>Innings:</strong> Each team bats and bowls once (Team winning
          toss decides)
        </p>
        <p>
          <strong>Balls Limit:</strong> Total balls = Overs × 6 (e.g., 20 overs =
          120 balls max)
        </p>
        <p>
          <strong>Scoring:</strong> Runs, wickets, overs, extras tracked. Economy
          rate auto-calculated
        </p>
        <p>
          <strong>Required:</strong> Toss winner, decision, batting/bowling stats,
          and match result
        </p>
        <p>
          <strong>Tip:</strong> Fill batting stats first, then bowling stats.
          Overs/balls will auto-calculate!
        </p>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Team 1 Name
        </label>
        <input
          type="text"
          value={localMatch.team1Name}
          onChange={(e) =>
            updateParent({ ...localMatch, team1Name: e.target.value })
          }
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter team 1 name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Team 2 Name
        </label>
        <input
          type="text"
          value={localMatch.team2Name}
          onChange={(e) =>
            updateParent({ ...localMatch, team2Name: e.target.value })
          }
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter team 2 name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Match Type
        </label>
        <select
          value={localMatch.matchType}
          onChange={(e) => {
            const newType = e.target.value;
            let newOvers = localMatch.totalOvers;
            if (newType === "t20") newOvers = 20;
            else if (newType === "odi") newOvers = 50;
            updateParent({
              ...localMatch,
              matchType: newType,
              totalOvers: newOvers,
            });
          }}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="t20">T20 (20 overs)</option>
          <option value="odi">ODI (50 overs)</option>
          <option value="limited-overs">Limited Overs (Custom)</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Total Overs
        </label>
        <div className="relative">
          <input
            type="number"
            value={localMatch.totalOvers}
            onChange={(e) => {
              const overs = Math.max(1, Math.min(50, Number(e.target.value)));
              updateParent({ ...localMatch, totalOvers: overs });
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            min="1"
            max="50"
          />
          <div className="text-xs text-gray-500 mt-1">
            Total balls: {localMatch.totalOvers * 6}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Toss Winner
        </label>
        <select
          value={localMatch.tossWinner || ""}
          onChange={(e) =>
            updateParent({ ...localMatch, tossWinner: e.target.value })
          }
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Select toss winner</option>
          <option value="team1">{localMatch.team1Name || "Team 1"}</option>
          <option value="team2">{localMatch.team2Name || "Team 2"}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Toss Decision
        </label>
        <select
          value={localMatch.tossDecision || ""}
          onChange={(e) =>
            updateParent({ ...localMatch, tossDecision: e.target.value })
          }
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Select decision</option>
          <option value="bat">Choose to Bat</option>
          <option value="bowl">Choose to Bowl</option>
        </select>
      </div>
    </div>
  </div>
);

const TeamManagementSection = ({
  localMatch,
  availableUsers,
  addPlayer,
  removePlayer,
  movePlayerUp,
  movePlayerDown,
}) => (
  <div className="space-y-8">
    {/* Team 1 */}
    <div className="bg-gray-50 rounded-lg p-6">
      <h4 className="text-xl font-semibold text-gray-800 mb-4">
        {localMatch.team1Name || "Team 1"} ({localMatch.team1Players.length}{" "}
        players)
      </h4>
      <PlayerSelector
        availableUsers={availableUsers}
        onAddPlayer={(userInfo, customName) =>
          addPlayer("team1", userInfo, customName)
        }
        selectedUserIds={[
          ...localMatch.team1Players
            .filter((p) => p.userId && !p.isCustom)
            .map((p) => p.userId),
          ...localMatch.team2Players
            .filter((p) => p.userId && !p.isCustom)
            .map((p) => p.userId),
        ]}
      />
      <div className="mt-4 space-y-2">
        {localMatch.team1Players.map((player, index) => (
          <div
            key={player.id}
            className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm"
          >
            <span className="font-medium">
              {index + 1}. {player.name} {player.isCustom && "(Custom)"}
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => movePlayerUp("team1", index)}
                className="text-blue-500 hover:text-blue-700 font-medium px-1"
                title="Move up"
              >
                ↑
              </button>
              <button
                onClick={() => movePlayerDown("team1", index)}
                className="text-blue-500 hover:text-blue-700 font-medium px-1"
                title="Move down"
              >
                ↓
              </button>
              <button
                onClick={() => removePlayer("team1", index)}
                className="text-red-500 hover:text-red-700 font-medium px-3 py-1 rounded hover:bg-red-50"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Team 2 */}
    <div className="bg-gray-50 rounded-lg p-6">
      <h4 className="text-xl font-semibold text-gray-800 mb-4">
        {localMatch.team2Name || "Team 2"} ({localMatch.team2Players.length}{" "}
        players)
      </h4>
      <PlayerSelector
        availableUsers={availableUsers}
        onAddPlayer={(userInfo, customName) =>
          addPlayer("team2", userInfo, customName)
        }
        selectedUserIds={[
          ...localMatch.team1Players
            .filter((p) => p.userId && !p.isCustom)
            .map((p) => p.userId),
          ...localMatch.team2Players
            .filter((p) => p.userId && !p.isCustom)
            .map((p) => p.userId),
        ]}
      />
      <div className="mt-4 space-y-2">
        {localMatch.team2Players.map((player, index) => (
          <div
            key={player.id}
            className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm"
          >
            <span className="font-medium">
              {index + 1}. {player.name} {player.isCustom && "(Custom)"}
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => movePlayerUp("team2", index)}
                className="text-blue-500 hover:text-blue-700 font-medium px-1"
                title="Move up"
              >
                ↑
              </button>
              <button
                onClick={() => movePlayerDown("team2", index)}
                className="text-blue-500 hover:text-blue-700 font-medium px-1"
                title="Move down"
              >
                ↓
              </button>
              <button
                onClick={() => removePlayer("team2", index)}
                className="text-red-500 hover:text-red-700 font-medium px-3 py-1 rounded hover:bg-red-50"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Team Balance Warning */}
    {localMatch.team1Players.length !== localMatch.team2Players.length && (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-800">
          ⚠️ Both teams must have equal number of players. Currently Team 1 has{" "}
          {localMatch.team1Players.length} and Team 2 has{" "}
          {localMatch.team2Players.length} players.
        </p>
      </div>
    )}
  </div>
);

const ScorecardSection = ({
  localMatch,
  updateBattingStats,
  updateBowlingStats,
  updateParent,
  movePlayerUp,
  movePlayerDown,
}) => {
  // Determine batting order based on toss decision
  let firstBattingTeam,
    secondBattingTeam,
    firstBowlingTeam,
    secondBowlingTeam;

  if (localMatch.tossWinner === "team1") {
    if (localMatch.tossDecision === "bat") {
      firstBattingTeam = "team1";
      secondBattingTeam = "team2";
      firstBowlingTeam = "team2"; // team2 bowls when team1 bats
      secondBowlingTeam = "team1"; // team1 bowls when team2 bats
    } else {
      // chose to bowl
      firstBattingTeam = "team2";
      secondBattingTeam = "team1";
      firstBowlingTeam = "team1"; // team1 bowls when team2 bats
      secondBowlingTeam = "team2"; // team2 bowls when team1 bats
    }
  } else if (localMatch.tossWinner === "team2") {
    if (localMatch.tossDecision === "bat") {
      firstBattingTeam = "team2";
      secondBattingTeam = "team1";
      firstBowlingTeam = "team1"; // team1 bowls when team2 bats
      secondBowlingTeam = "team2"; // team2 bowls when team1 bats
    } else {
      // chose to bowl
      firstBattingTeam = "team1";
      secondBattingTeam = "team2";
      firstBowlingTeam = "team2"; // team2 bowls when team1 bats
      secondBowlingTeam = "team1"; // team1 bowls when team2 bats
    }
  } else {
    // Default order if no toss winner selected
    firstBattingTeam = "team1";
    secondBattingTeam = "team2";
    firstBowlingTeam = "team2";
    secondBowlingTeam = "team1";
  }

  return (
    <div className="space-y-8">
      {/* First Innings */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6">
        <h4 className="text-xl font-semibold text-blue-800 mb-2">
          {firstBattingTeam === "team1"
            ? localMatch.team1Name || "Team 1"
            : localMatch.team2Name || "Team 2"}{" "}
          Innings (1st Batting)
        </h4>
        <div className="text-sm text-blue-600 mb-4">
          {localMatch.tossWinner && localMatch.tossDecision && (
            <span>
              {localMatch.tossWinner === "team1"
                ? localMatch.team1Name || "Team 1"
                : localMatch.team2Name || "Team 2"}{" "}
              won the toss and chose to{" "}
              {localMatch.tossDecision === "bat" ? "bat first" : "bowl first"}
            </span>
          )}
        </div>
        <InningsScorecard
          battingTeam={firstBattingTeam}
          bowlingTeam={firstBowlingTeam}
          match={localMatch}
          onUpdateBatting={updateBattingStats}
          onUpdateBowling={updateBowlingStats}
          onUpdateMatch={updateParent}
          inningsNumber={1}
          onMovePlayerUp={movePlayerUp}
          onMovePlayerDown={movePlayerDown}
        />
      </div>

      {/* Second Innings */}
      <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6">
        <h4 className="text-xl font-semibold text-green-800 mb-2">
          {secondBattingTeam === "team1"
            ? localMatch.team1Name || "Team 1"
            : localMatch.team2Name || "Team 2"}{" "}
          Innings (2nd Batting)
        </h4>
        <div className="text-sm text-green-600 mb-4">
          <span>
            Chasing{" "}
            {localMatch.innings[firstBattingTeam].totalRuns > 0
              ? `${localMatch.innings[firstBattingTeam].totalRuns + 1} runs to win`
              : "target to be set"}
          </span>
        </div>
        <InningsScorecard
          battingTeam={secondBattingTeam}
          bowlingTeam={secondBowlingTeam}
          match={localMatch}
          onUpdateBatting={updateBattingStats}
          onUpdateBowling={updateBowlingStats}
          onUpdateMatch={updateParent}
          inningsNumber={2}
          onMovePlayerUp={movePlayerUp}
          onMovePlayerDown={movePlayerDown}
        />
      </div>

      {/* Match Result */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h4 className="text-xl font-semibold text-yellow-800 mb-4">
          Match Result
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Winner
            </label>
            <select
              value={localMatch.winner || ""}
              onChange={(e) =>
                updateParent({ ...localMatch, winner: e.target.value })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="">Select winner</option>
              <option value="team1">{localMatch.team1Name || "Team 1"}</option>
              <option value="team2">{localMatch.team2Name || "Team 2"}</option>
              <option value="draw">Draw/Tie</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Result Description
            </label>
            <input
              type="text"
              value={localMatch.result}
              onChange={(e) =>
                updateParent({ ...localMatch, result: e.target.value })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              placeholder="e.g., Team A won by 5 wickets"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const MatchSummary = ({ localMatch }) => {
  // Determine batting order for summary display
  let firstBattingTeam, secondBattingTeam;

  if (localMatch.tossWinner === "team1") {
    if (localMatch.tossDecision === "bat") {
      firstBattingTeam = "team1";
      secondBattingTeam = "team2";
    } else {
      firstBattingTeam = "team2";
      secondBattingTeam = "team1";
    }
  } else if (localMatch.tossWinner === "team2") {
    if (localMatch.tossDecision === "bat") {
      firstBattingTeam = "team2";
      secondBattingTeam = "team1";
    } else {
      firstBattingTeam = "team1";
      secondBattingTeam = "team2";
    }
  } else {
    firstBattingTeam = "team1";
    secondBattingTeam = "team2";
  }

  return (
    <div className="mt-8 bg-white rounded-lg shadow-md p-6">
      <h4 className="text-lg font-semibold text-gray-800 mb-4">Match Summary</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="text-center">
          <h5 className="font-semibold text-blue-600 mb-2">
            {firstBattingTeam === "team1"
              ? localMatch.team1Name || "Team 1"
              : localMatch.team2Name || "Team 2"}{" "}
            (1st Batting)
          </h5>
          <p className="text-2xl font-bold text-gray-800">
            {localMatch.innings[firstBattingTeam].totalRuns}/
            {localMatch.innings[firstBattingTeam].wickets}
          </p>
          <p className="text-sm text-gray-600">
            ({localMatch.innings[firstBattingTeam].overs}.
            {localMatch.innings[firstBattingTeam].balls} overs)
          </p>
        </div>
        <div className="text-center">
          <h5 className="font-semibold text-green-600 mb-2">
            {secondBattingTeam === "team1"
              ? localMatch.team1Name || "Team 1"
              : localMatch.team2Name || "Team 2"}{" "}
            (2nd Batting)
          </h5>
          <p className="text-2xl font-bold text-gray-800">
            {localMatch.innings[secondBattingTeam].totalRuns}/
            {localMatch.innings[secondBattingTeam].wickets}
          </p>
          <p className="text-sm text-gray-600">
            ({localMatch.innings[secondBattingTeam].overs}.
            {localMatch.innings[secondBattingTeam].balls} overs)
          </p>
        </div>
      </div>
      {localMatch.result && (
        <div className="mt-4 text-center">
          <p className="text-lg font-semibold text-gray-700">
            {localMatch.result}
          </p>
        </div>
      )}
    </div>
  );
};

const InningsScorecard = ({
  battingTeam,
  bowlingTeam,
  match,
  onUpdateBatting,
  onUpdateBowling,
  onUpdateMatch,
  inningsNumber,
  onMovePlayerUp,
  onMovePlayerDown,
}) => {
  const innings = match.innings[battingTeam];
  const battingPlayers = match[`${battingTeam}Players`];
  const bowlingPlayers = match[`${bowlingTeam}Players`];

  const updateExtras = (field, value) => {
    const updated = { ...match };
    updated.innings[battingTeam].extras[field] = Math.max(
      0,
      Number(value) || 0
    );

    // Recalculate total
    const battingRuns = innings.battingStats.reduce(
      (total, player) => total + (player.runs || 0),
      0
    );
    const extrasTotal = Object.values(updated.innings[battingTeam].extras).reduce(
      (total, extra) => total + (extra || 0),
      0
    );
    updated.innings[battingTeam].totalRuns = battingRuns + extrasTotal;

    onUpdateMatch(updated);
  };

  return (
    <div className="space-y-6">
      {/* Innings Summary */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-sm text-gray-600">Total Runs</p>
            <p className="text-xl font-bold text-gray-800">
              {innings.totalRuns}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Wickets</p>
            <p className="text-xl font-bold text-gray-800">{innings.wickets}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Overs</p>
            <p className="text-xl font-bold text-gray-800">
              {innings.overs}.{innings.balls}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Run Rate</p>
            <p className="text-xl font-bold text-gray-800">
              {innings.overs > 0
                ? (innings.totalRuns / innings.overs).toFixed(2)
                : "0.00"}
            </p>
          </div>
        </div>
      </div>

      {/* Batting Stats - Show batting team's players */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h5 className="font-semibold text-gray-800 mb-3">
          Batting Statistics -{" "}
          {battingTeam === "team1"
            ? match.team1Name || "Team 1"
            : match.team2Name || "Team 2"}
        </h5>
        <div className="text-xs text-gray-600 mb-3">
          💡 Tip: Use the ↑↓ buttons to arrange batting order. Players listed
          first bat first.
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 text-left">Batting Order</th>
                <th className="p-2 text-left">Batsman</th>
                <th className="p-2 text-center">Runs</th>
                <th className="p-2 text-center">Balls</th>
                <th className="p-2 text-center">4s</th>
                <th className="p-2 text-center">6s</th>
                <th className="p-2 text-center">S/R</th>
                <th className="p-2 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {battingPlayers.map((player, index) => {
                const stats =
                  innings.battingStats.find(
                    (s) => String(s.playerId) === String(player.userId)
                  ) || {
                    runs: 0,
                    balls: 0,
                    fours: 0,
                    sixes: 0,
                    isOut: false,
                  };
                const strikeRate =
                  stats.balls > 0
                    ? ((stats.runs / stats.balls) * 100).toFixed(1)
                    : "0.0";

                return (
                  <tr key={player.id} className="border-b">
                    <td className="p-2">
                      <div className="flex items-center space-x-1">
                        <span className="text-sm font-medium text-gray-700 min-w-[20px]">
                          {index + 1}.
                        </span>
                        <div className="flex flex-col">
                          <button
                            onClick={() => onMovePlayerUp(battingTeam, index)}
                            disabled={index === 0}
                            className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                            title="Move up"
                          >
                            ↑
                          </button>
                          <button
                            onClick={() => onMovePlayerDown(battingTeam, index)}
                            disabled={index === battingPlayers.length - 1}
                            className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                            title="Move down"
                          >
                            ↓
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="p-2 font-medium">{player.name}</td>
                    <td className="p-2 text-center">
                      <input
                        type="number"
                        value={stats.runs}
                        onChange={(e) =>
                          onUpdateBatting(
                            battingTeam,
                            player.userId,
                            "runs",
                            Number(e.target.value) || 0
                          )
                        }
                        className="w-16 px-2 py-1 border rounded text-center"
                        min="0"
                      />
                    </td>
                    <td className="p-2 text-center">
                      <input
                        type="number"
                        value={stats.balls}
                        onChange={(e) => {
                          const maxBalls = match.totalOvers * 6;
                          const value = Math.min(
                            maxBalls,
                            Math.max(0, Number(e.target.value) || 0)
                          );
                          onUpdateBatting(
                            battingTeam,
                            player.userId,
                            "balls",
                            value
                          );
                        }}
                        className="w-16 px-2 py-1 border rounded text-center"
                        min="0"
                        max={match.totalOvers * 6}
                        title={`Max ${match.totalOvers * 6} balls (${match.totalOvers} overs)`}
                      />
                    </td>
                    <td className="p-2 text-center">
                      <input
                        type="number"
                        value={stats.fours}
                        onChange={(e) =>
                          onUpdateBatting(
                            battingTeam,
                            player.userId,
                            "fours",
                            Number(e.target.value) || 0
                          )
                        }
                        className="w-16 px-2 py-1 border rounded text-center"
                        min="0"
                      />
                    </td>
                    <td className="p-2 text-center">
                      <input
                        type="number"
                        value={stats.sixes}
                        onChange={(e) =>
                          onUpdateBatting(
                            battingTeam,
                            player.userId,
                            "sixes",
                            Number(e.target.value) || 0
                          )
                        }
                        className="w-16 px-2 py-1 border rounded text-center"
                        min="0"
                      />
                    </td>
                    <td className="p-2 text-center">{strikeRate}</td>
                    <td className="p-2 text-center">
                      <input
                        type="checkbox"
                        checked={stats.isOut}
                        onChange={(e) =>
                          onUpdateBatting(
                            battingTeam,
                            player.userId,
                            "isOut",
                            e.target.checked
                          )
                        }
                        className="rounded"
                      />
                      <span className="ml-1 text-xs">
                        {stats.isOut ? "Out" : "Not Out"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bowling Stats - Show bowling team's players */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h5 className="font-semibold text-gray-800 mb-3">
          Bowling Statistics -{" "}
          {bowlingTeam === "team1"
            ? match.team1Name || "Team 1"
            : match.team2Name || "Team 2"}
        </h5>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 text-left">Bowler</th>
                <th className="p-2 text-center">Overs</th>
                <th className="p-2 text-center">Balls</th>
                <th className="p-2 text-center">Maidens</th>
                <th className="p-2 text-center">Runs</th>
                <th className="p-2 text-center">Wickets</th>
                <th className="p-2 text-center">Economy</th>
              </tr>
            </thead>
            <tbody>
              {bowlingPlayers.map((player) => {
                // Look for bowling stats in the batting team's innings (since they bowl to the batting team)
                const stats =
                  innings.bowlingStats.find(
                    (s) => String(s.playerId) === String(player.userId)
                  ) || {
                    overs: 0,
                    balls: 0,
                    maidens: 0,
                    runs: 0,
                    wickets: 0,
                    economy: 0,
                  };

                return (
                  <tr key={player.id} className="border-b">
                    <td className="p-2 font-medium">{player.name}</td>
                    <td className="p-2 text-center">
                      <input
                        type="number"
                        value={stats.overs}
                        onChange={(e) =>
                          onUpdateBowling(
                            battingTeam, // Update bowling stats in the batting team's innings
                            player.userId,
                            "overs",
                            Number(e.target.value) || 0
                          )
                        }
                        className="w-16 px-2 py-1 border rounded text-center"
                        min="0"
                        max={match.totalOvers}
                      />
                    </td>
                    <td className="p-2 text-center">
                      <input
                        type="number"
                        value={stats.balls}
                        onChange={(e) =>
                          onUpdateBowling(
                            battingTeam, // Update bowling stats in the batting team's innings
                            player.userId,
                            "balls",
                            Number(e.target.value) || 0
                          )
                        }
                        className="w-16 px-2 py-1 border rounded text-center"
                        min="0"
                        max="5"
                      />
                    </td>
                    <td className="p-2 text-center">
                      <input
                        type="number"
                        value={stats.maidens}
                        onChange={(e) =>
                          onUpdateBowling(
                            battingTeam, // Update bowling stats in the batting team's innings
                            player.userId,
                            "maidens",
                            Number(e.target.value) || 0
                          )
                        }
                        className="w-16 px-2 py-1 border rounded text-center"
                        min="0"
                        max={stats.overs}
                      />
                    </td>
                    <td className="p-2 text-center">
                      <input
                        type="number"
                        value={stats.runs}
                        onChange={(e) =>
                          onUpdateBowling(
                            battingTeam, // Update bowling stats in the batting team's innings
                            player.userId,
                            "runs",
                            Number(e.target.value) || 0
                          )
                        }
                        className="w-16 px-2 py-1 border rounded text-center"
                        min="0"
                      />
                    </td>
                    <td className="p-2 text-center">
                      <input
                        type="number"
                        value={stats.wickets}
                        onChange={(e) =>
                          onUpdateBowling(
                            battingTeam, // Update bowling stats in the batting team's innings
                            player.userId,
                            "wickets",
                            Number(e.target.value) || 0
                          )
                        }
                        className="w-16 px-2 py-1 border rounded text-center"
                        min="0"
                        max="10"
                      />
                    </td>
                    <td className="p-2 text-center">{stats.economy}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Extras */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h5 className="font-semibold text-gray-800 mb-3">Extras</h5>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Object.entries(innings.extras).map(([key, value]) => (
            <div key={key}>
              <label className="block text-xs text-gray-600 mb-1 capitalize">
                {key.replace(/([A-Z])/g, " $1")}
              </label>
              <input
                type="number"
                value={value}
                onChange={(e) => updateExtras(key, e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-center"
                min="0"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PlayerSelector = ({
  availableUsers,
  onAddPlayer,
  selectedUserIds = [],
}) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [customName, setCustomName] = useState("");
  const [useCustomName, setUseCustomName] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Fetch user suggestions from API
  const fetchUserSuggestions = async (term) => {
    if (!term || term.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/common/search-users?term=${term}`
      );
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      }
    } catch (error) {
      console.error("Error fetching user suggestions:", error);
      setSuggestions([]);
    }
  };

  // Debounced search
  React.useEffect(() => {
    if (!useCustomName && searchTerm) {
      const timer = setTimeout(() => {
        fetchUserSuggestions(searchTerm);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
    }
  }, [searchTerm, useCustomName]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowSuggestions(true);
    setSelectedUser(null);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchTerm(user.full_name || user.username);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleAdd = () => {
    if (useCustomName && customName.trim()) {
      onAddPlayer(null, customName.trim());
      setCustomName("");
    } else if (!useCustomName && selectedUser) {
      onAddPlayer(selectedUser);
      setSelectedUser(null);
      setSearchTerm("");
    }
  };

  return (
    <div className="flex flex-wrap gap-3 items-center relative">
      {!useCustomName ? (
        <>
          <div className="relative flex-1 min-w-[250px]">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => {
                // Delay to allow click on suggestion
                setTimeout(() => setShowSuggestions(false), 200);
              }}
              placeholder="Search for system users..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 rounded-b-lg max-h-[200px] overflow-y-auto z-[1000] shadow-lg">
                {suggestions.map((user) => {
                  const isAlreadySelected = selectedUserIds.includes(
                    user.id || user.user_id
                  );
                  return (
                    <div
                      key={user.id || user.user_id}
                      onClick={() =>
                        !isAlreadySelected && handleSelectUser(user)
                      }
                      className="px-3 py-2 border-b border-gray-100 transition-colors"
                      style={{
                        opacity: isAlreadySelected ? 0.5 : 1,
                        cursor: isAlreadySelected ? "not-allowed" : "pointer",
                        backgroundColor: isAlreadySelected ? "#f0f0f0" : "white",
                      }}
                      onMouseEnter={(e) => {
                        if (!isAlreadySelected) {
                          e.currentTarget.style.backgroundColor = "#f7fafc";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isAlreadySelected) {
                          e.currentTarget.style.backgroundColor = "white";
                        }
                      }}
                    >
                      <div className="font-medium">
                        {user.full_name || user.username}
                        {isAlreadySelected && (
                          <span style={{ color: "#e53e3e", marginLeft: "8px" }}>
                            (Already Added)
                          </span>
                        )}
                      </div>
                      {user.email && (
                        <div className="text-xs text-gray-600">{user.email}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {showSuggestions && searchTerm.length >= 2 && suggestions.length === 0 && (
              <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 rounded-b-lg z-[1000] shadow-lg">
                <div className="px-3 py-2 text-sm text-gray-600 text-center">
                  No users found
                </div>
              </div>
            )}
          </div>
          <button
            onClick={handleAdd}
            disabled={!selectedUser}
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
          >
            Add Player
          </button>
        </>
      ) : (
        <>
          <input
            type="text"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="Enter custom player name"
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleAdd}
            disabled={!customName.trim()}
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
          >
            Add Player
          </button>
        </>
      )}
      <button
        onClick={() => setUseCustomName(!useCustomName)}
        className="text-blue-600 hover:text-blue-800 text-sm underline"
      >
        {useCustomName ? "Use system users" : "Add custom name"}
      </button>
    </div>
  );
};

export default CricketSummaryTab;

